import React, { useEffect, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

// Layout
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";

// Eager load Home page for fast initial render
import Home from "./pages/Home";

// Auth Pages
const UnifiedLogin = lazy(() => import("./pages/auth/Login"));
const AppPicker = lazy(() => import("./pages/AppPicker"));
const AppNotAvailable = lazy(() => import("./pages/AppNotAvailable"));

// New Unified Switchboard
const Switchboard = lazy(() => import("./pages/app/Switchboard"));

// Unified Admin Portal
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminHome = lazy(() => import("./pages/admin/AdminHome"));
const EcomAdminRedirect = lazy(() => import("./pages/admin/EcomAdmin"));
const InkFloAdminRedirect = lazy(() => import("./pages/admin/InkFloAdminRedirect"));
// LocalAdmin removed (legacy)
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));

// Customer Dashboards
const CustomerEcomDashboard = lazy(() => import("./pages/customer/EcomDashboard"));
const CustomerInkFloDashboard = lazy(() => import("./pages/customer/InkFloDashboard"));
// CustomerLocalDashboard removed (legacy)

// Lazy load other pages for better performance
// Legacy imports removed (Solutions, OurSolutions, HowItWorks, WhoWeServe, Results, EcomReport, Report)
// were tied to prior positioning.
const Academy = lazy(() => import("./pages/Academy"));
// HowItWorks + WhoWeServe removed (legacy)
// Results removed (legacy)
const Industries = lazy(() => import("./pages/Industries"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const Demo = lazy(() => import("./pages/Demo"));
// Legacy imports removed (TestAgent, KaiyaLocal, KaiyaSchools, KaiyaECom)
// EcomReport removed (legacy)
const EcomProtectLanding = lazy(() => import("./pages/EcomProtectStandard"));
// Report removed (legacy)
// Legacy Calculator removed
const Privacy = lazy(() => import("./pages/Privacy"));
// InkFlo Pages
const InkFlo = lazy(() => import("./pages/InkFlo"));
const InkFloGetStarted = lazy(() => import("./pages/inkflo/GetStarted"));
const InkFloOnboard = lazy(() => import("./pages/inkflo/Onboard"));
const InkFloAdmin = lazy(() => import("./pages/inkflo/Admin"));
const InkFloShop = lazy(() => import("./pages/inkflo/Shop"));
const InkFloCustomerDashboard = lazy(() => import("./pages/inkflo/CustomerDashboard"));
const InkFloCustomerShopDetail = lazy(() => import("./pages/inkflo/CustomerShopDetail"));

// Admin Pages
const AdminHealthCheck = lazy(() => import("./pages/admin/HealthCheck"));

// ManifestGuard Pages
const ManifestGuard = lazy(() => import("./pages/ManifestGuard"));
const ManifestGuardTerms = lazy(() => import("./pages/legal/ManifestGuardTerms"));

// VendorGuard Public Page
const VendorGuardLanding = lazy(() => import("./pages/VendorGuard"));

// New Risk Infrastructure Pages
const RiskInfrastructure = lazy(() => import("./pages/RiskInfrastructure"));
const ProtectionLayers = lazy(() => import("./pages/ProtectionLayers"));
// Legacy import removed (CommunicationsSystems)
const RiskAssessment = lazy(() => import("./pages/RiskAssessment"));

// How It Works Pages for Each Module
const HowVendorGuardWorks = lazy(() => import("./pages/HowVendorGuardWorks"));
const HowGrantGuardWorks = lazy(() => import("./pages/HowGrantGuardWorks"));
const HowProofWorks = lazy(() => import("./pages/HowProofWorks"));
const HowManifestGuardWorks = lazy(() => import("./pages/HowManifestGuardWorks"));
const HowEcomProtectWorks = lazy(() => import("./pages/HowEcomProtectWorks"));

// Platform Pages
const KaiyaPlatform = lazy(() => import("./pages/KaiyaPlatform"));
const HowKaiyaWorks = lazy(() => import("./pages/HowKaiyaWorks"));
const SecurityPage = lazy(() => import("./pages/Security"));
const RiskSignals = lazy(() => import("./pages/RiskSignals"));
const OperationalEvidence = lazy(() => import("./pages/OperationalEvidence"));
const EvidenceVerification = lazy(() => import("./pages/EvidenceVerification"));
const EvidenceLedger = lazy(() => import("./pages/EvidenceLedger"));

// GrantGuard Public Landing Page
const GrantGuardLanding = lazy(() => import("./pages/GrantGuard"));

// PROOF Public Landing Page
const ProofLanding = lazy(() => import("./pages/ProofLanding"));
const ManifestGuardLayout = lazy(() => import("./pages/manifestguard/Layout"));
const ManifestGuardDashboard = lazy(() => import("./pages/manifestguard/Dashboard"));
const ManifestGuardNewShipment = lazy(() => import("./pages/manifestguard/NewShipment"));
const ManifestGuardShipmentsList = lazy(() => import("./pages/manifestguard/ShipmentsList"));
const ManifestGuardShipmentDetail = lazy(() => import("./pages/manifestguard/ShipmentDetail"));
const ManifestGuardAuditLog = lazy(() => import("./pages/manifestguard/AuditLog"));
const ManifestGuardSettings = lazy(() => import("./pages/manifestguard/Settings"));

// GrantGuard Pages
const GrantGuardAppRoutes = lazy(() => import("./routes/GrantGuardAppRoutes"));
const GrantGuardTribal = lazy(() => import("./pages/GrantGuardTribalStandard"));

// CouncilGuard Pages
const CouncilGuardLanding = lazy(() => import("./pages/CouncilGuard"));
const ClerkAssistLanding = lazy(() => import("./pages/ClerkAssistLanding"));
const CouncilGuardAppRoutes = lazy(() => import("./routes/CouncilGuardAppRoutes"));
const CouncilGuardAIDisclosure = lazy(() => import("./pages/councilguard/PolicyPages").then(m => ({ default: m.CouncilGuardAIDisclosure })));
const CouncilGuardRecordsDisclaimer = lazy(() => import("./pages/councilguard/PolicyPages").then(m => ({ default: m.CouncilGuardRecordsDisclaimer })));
const CouncilGuardSecurity = lazy(() => import("./pages/councilguard/PolicyPages").then(m => ({ default: m.CouncilGuardSecurity })));
const CouncilGuardSupport = lazy(() => import("./pages/councilguard/PolicyPages").then(m => ({ default: m.CouncilGuardSupport })));
const CouncilGuardPublicPortal = lazy(() => import("./pages/councilguard/PublicPortal"));

// PROOF Pages
const ProofLayout = lazy(() => import("./pages/proof/Layout"));
const ProofDashboard = lazy(() => import("./pages/proof/Dashboard"));
const ProofNewRecord = lazy(() => import("./pages/proof/NewRecord"));
const ProofRecordDetail = lazy(() => import("./pages/proof/RecordDetail"));
const ProofVendors = lazy(() => import("./pages/proof/Vendors"));
const ProofCustomerSession = lazy(() => import("./pages/proof/CustomerSession"));
const ProofDemo = lazy(() => import("./pages/proof/Demo"));
const ProofSharedRecord = lazy(() => import("./pages/proof/SharedRecord"));
const ProofRecordCompleted = lazy(() => import("./pages/proof/RecordCompleted"));
const ProofWhyProof = lazy(() => import("./pages/proof/WhyProof"));
const ProofPricing = lazy(() => import("./pages/proof/Pricing"));
const ProofComplianceDashboard = lazy(() => import("./pages/proof/ComplianceDashboard"));
const ProofComplianceSettings = lazy(() => import("./pages/proof/ComplianceSettings"));

// COA Admin Pages
const COAAdmin = lazy(() => import("./pages/admin/COAAdmin"));
const COATierManagement = lazy(() => import("./pages/admin/COATierManagement"));

// Admin Automation & Evidence Dashboards
const AdminAutomationDashboard = lazy(() => import("./pages/admin/AutomationDashboard"));
const AdminEvidenceDashboard = lazy(() => import("./pages/admin/EvidenceDashboard"));
const AdminAlerts = lazy(() => import("./pages/admin/AdminAlerts"));
const AIAgentsHub = lazy(() => import("./pages/admin/AIAgentsHub"));

// Risk Advisor Pages
const RiskDashboard = lazy(() => import("./pages/admin/RiskDashboard"));
const RiskAdvisorChat = lazy(() => import("./pages/admin/RiskAdvisorChat"));
const KnowledgeVault = lazy(() => import("./pages/admin/KnowledgeVault"));

// Social Publisher (Blotato Integration)
const SocialPublisher = lazy(() => import("./pages/admin/SocialPublisher"));

// Public Verification Page
const VerifyPage = lazy(() => import("./pages/VerifyPage"));

// ContractorShield Pages (Coming Soon)
const ContractorShieldComingSoon = lazy(() => import("./pages/contractorshield/ComingSoon"));

// VendorGuard Pages
const VendorGuardLayout = lazy(() => import("./pages/vendorguard/Layout"));
const VendorGuardDashboard = lazy(() => import("./pages/vendorguard/Dashboard"));
const VendorGuardVendors = lazy(() => import("./pages/vendorguard/Vendors"));
const VendorGuardAddVendor = lazy(() => import("./pages/vendorguard/AddVendor"));
const VendorGuardVendorDetail = lazy(() => import("./pages/vendorguard/VendorDetail"));
const VendorGuardAuditLog = lazy(() => import("./pages/vendorguard/AuditLog"));
const VendorGuardSettings = lazy(() => import("./pages/vendorguard/Settings"));
const VendorOnboarding = lazy(() => import("./pages/vendorguard/VendorOnboarding"));

// Risk Intelligence Engine Pages
const RiskIntelligenceLayout = lazy(() => import("./pages/risk-intelligence/Layout"));
const RiskIntelligenceDashboard = lazy(() => import("./pages/risk-intelligence/Dashboard"));
const RiskIntelligenceContent = lazy(() => import("./pages/risk-intelligence/ContentLibrary"));
const RiskIntelligenceLeads = lazy(() => import("./pages/risk-intelligence/Leads"));
const RiskIntelligenceAutomation = lazy(() => import("./pages/risk-intelligence/Automation"));
const RiskIntelligenceReports = lazy(() => import("./pages/risk-intelligence/Reports"));
const RiskIntelligenceLeadsCRM = lazy(() => import("./pages/risk-intelligence/LeadsCRM"));
const RiskIntelligenceSearchIntel = lazy(() => import("./pages/risk-intelligence/SearchIntelligence"));
const RiskIntelligenceSocial = lazy(() => import("./pages/risk-intelligence/SocialAdmin"));

// Risk Intelligence Public Blog
const RiskIntelligenceBlog = lazy(() => import("./pages/RiskIntelligenceBlog"));
const RiskIntelligenceCategory = lazy(() => import("./pages/RiskIntelligenceCategory"));
const RiskIntelligenceArticle = lazy(() => import("./pages/RiskIntelligenceArticle"));
const RiskIntelligenceReport = lazy(() => import("./pages/RiskIntelligenceReport"));
const RiskIntelligenceCheckoutSuccess = lazy(() => import("./pages/RiskIntelligenceCheckoutSuccess"));
const RiskIntelligenceProducts = lazy(() => import("./pages/RiskIntelligenceProducts"));

// Public Pages
const FreeAuditTool = lazy(() => import("./pages/public/FreeAuditTool"));
const RiskScanner = lazy(() => import("./pages/RiskScanner"));

// Communications Landing Page
// Legacy import removed (CommunicationsLanding)

// Legal Pages
const LegalHub = lazy(() => import("./pages/legal/LegalHub"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const RefundPolicy = lazy(() => import("./pages/legal/RefundPolicy"));
const AcceptableUse = lazy(() => import("./pages/legal/AcceptableUse"));
const AIDisclaimer = lazy(() => import("./pages/legal/AIDisclaimer"));
const EmailCompliance = lazy(() => import("./pages/legal/EmailCompliance"));
const SecurityPolicy = lazy(() => import("./pages/legal/SecurityPolicy"));
const DataProcessingAddendum = lazy(() => import("./pages/legal/DataProcessingAddendum"));
const ShopifyDisclaimer = lazy(() => import("./pages/legal/ShopifyDisclaimer"));
const CustomerAuthorization = lazy(() => import("./pages/legal/CustomerAuthorization"));
const ProofModuleTerms = lazy(() => import("./pages/legal/ProofModuleTerms"));
const RiskDisclosure = lazy(() => import("./pages/legal/RiskDisclosure"));

// Auth Pages
const Signup = lazy(() => import("./pages/auth/Signup"));

// ECom Protect Admin Pages (lazy loaded)
const EcomLogin = lazy(() => import("./pages/admin/EcomProtect/Login").then(m => ({ default: m.EcomLogin })));
const EcomResetPassword = lazy(() => import("./pages/admin/EcomProtect/ResetPassword"));
const EcomLayout = lazy(() => import("./pages/admin/EcomProtect/Layout"));
const EcomDashboard = lazy(() => import("./pages/admin/EcomProtect/Dashboard"));
const EcomClients = lazy(() => import("./pages/admin/EcomProtect/Clients"));
const EcomClientDetail = lazy(() => import("./pages/admin/EcomProtect/ClientDetail"));
const EcomConnections = lazy(() => import("./pages/admin/EcomProtect/Connections"));
const EcomCases = lazy(() => import("./pages/admin/EcomProtect/Cases"));
const EcomEscalations = lazy(() => import("./pages/admin/EcomProtect/Escalations"));
const EcomReports = lazy(() => import("./pages/admin/EcomProtect/Reports"));
const EcomSettings = lazy(() => import("./pages/admin/EcomProtect/Settings"));
const EcomTestTools = lazy(() => import("./pages/admin/EcomProtect/TestTools"));
const EcomScheduler = lazy(() => import("./pages/admin/EcomProtect/Scheduler"));
const EcomWebhooks = lazy(() => import("./pages/admin/EcomProtect/Webhooks"));
const AdminLeads = lazy(() => import("./pages/admin/EcomProtect/Leads"));
const EcomEmailGateway = lazy(() => import("./pages/admin/EcomProtect/EmailGateway"));
const EcomDisputes = lazy(() => import("./pages/admin/EcomProtect/Disputes"));

// Customer Portal Pages (lazy loaded)
const PortalLogin = lazy(() => import("./pages/portal/Login"));
const PortalResetPassword = lazy(() => import("./pages/portal/ResetPassword"));
const PortalLayout = lazy(() => import("./pages/portal/Layout"));
const PortalDashboard = lazy(() => import("./pages/portal/Dashboard"));
const PortalOnboarding = lazy(() => import("./pages/portal/Onboarding"));
const PortalConnections = lazy(() => import("./pages/portal/Connections"));
const PortalPolicies = lazy(() => import("./pages/portal/Policies"));
const PortalCases = lazy(() => import("./pages/portal/Cases"));
const PortalRecovery = lazy(() => import("./pages/portal/Recovery"));
const PortalReports = lazy(() => import("./pages/portal/Reports"));
const PortalSettings = lazy(() => import("./pages/portal/Settings"));
const InviteAccept = lazy(() => import("./pages/portal/InviteAccept"));

// Intake Funnel Pages (lazy loaded)
// Legacy intake imports removed

// Loading fallback for lazy-loaded pages
const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center bg-[#0a1929]">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-3 border-[#48a8ab]/20 border-t-[#48a8ab] rounded-full animate-spin" />
      <p className="text-gray-400 text-sm">Loading...</p>
    </div>
  </div>
);

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const MainLayout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

// Main site routes wrapped in MainLayout
const MainRoutes = () => (
  <MainLayout>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/academy" element={<Academy />} />
        <Route path="/industries" element={<Industries />} />
        <Route path="/case-studies" element={<CaseStudies />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/demo" element={<Demo />} />
        {/* Legacy routes removed (tied to prior positioning) — fall through to Home */}
        <Route path="/ecom-protect" element={<EcomProtectLanding />} />
        {/* Legacy /calculator route removed */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/inkflo" element={<InkFlo />} />
        <Route path="/inkflo/get-started" element={<InkFloGetStarted />} />
        <Route path="/inkflo/onboard" element={<InkFloOnboard />} />
        {/* Legacy /calculator route removed */}
        {/* New Risk Infrastructure Pages */}
        <Route path="/risk-infrastructure" element={<RiskInfrastructure />} />
        <Route path="/protection-layers" element={<ProtectionLayers />} />
        <Route path="/risk-assessment" element={<RiskAssessment />} />
        {/* Risk Scanner (Public Lead Capture) */}
        <Route path="/risk-scanner" element={<RiskScanner />} />
        {/* How It Works Pages for Each Module */}
        <Route path="/how-vendorguard-works" element={<HowVendorGuardWorks />} />
        <Route path="/how-grantguard-works" element={<HowGrantGuardWorks />} />
        <Route path="/how-proof-works" element={<HowProofWorks />} />
        <Route path="/how-manifestguard-works" element={<HowManifestGuardWorks />} />
        <Route path="/how-ecom-protect-works" element={<HowEcomProtectWorks />} />
        {/* Platform Overview Pages */}
        <Route path="/kaiya-platform" element={<KaiyaPlatform />} />
        <Route path="/how-kaiya-works" element={<HowKaiyaWorks />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/risk-signals" element={<RiskSignals />} />
        <Route path="/operational-evidence" element={<OperationalEvidence />} />
        <Route path="/evidence-ledger" element={<EvidenceLedger />} />
        <Route path="/verify/evidence/:verification_id" element={<EvidenceVerification />} />
        <Route path="/verify/:verification_id" element={<EvidenceVerification />} />
        {/* ManifestGuard Public Pages */}
        <Route path="/manifestguard" element={<ManifestGuard />} />
        <Route path="/manifestguard/terms" element={<ManifestGuardTerms />} />
        {/* VendorGuard Public Landing Page */}
        <Route path="/vendorguard" element={<VendorGuardLanding />} />
        {/* VendorGuard Public Onboarding (no auth required) */}
        <Route path="/onboarding/vendorguard/:token" element={<VendorOnboarding />} />
        {/* GrantGuard Public Landing Page */}
        <Route path="/grantguard" element={<GrantGuardLanding />} />
        <Route path="/grantguard-tribal" element={<GrantGuardTribal />} />
        {/* CouncilGuard Public Landing Page */}
        <Route path="/councilguard" element={<CouncilGuardLanding />} />
        <Route path="/clerkassist" element={<ClerkAssistLanding />} />
        <Route path="/councilguard/ai-disclosure" element={<CouncilGuardAIDisclosure />} />
        <Route path="/councilguard/records-disclaimer" element={<CouncilGuardRecordsDisclaimer />} />
        <Route path="/councilguard/security" element={<CouncilGuardSecurity />} />
        <Route path="/councilguard/support" element={<CouncilGuardSupport />} />
        <Route path="/councilguard/public/:orgId" element={<CouncilGuardPublicPortal />} />
        {/* PROOF Public Landing Page */}
        <Route path="/proof" element={<ProofLanding />} />
        {/* ContractorShield Coming Soon */}
        <Route path="/contractorshield" element={<ContractorShieldComingSoon />} />
        {/* Legal Pages */}
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/acceptable-use" element={<AcceptableUse />} />
        <Route path="/ai-disclaimer" element={<AIDisclaimer />} />
        <Route path="/email-compliance" element={<EmailCompliance />} />
        <Route path="/security-policy" element={<SecurityPolicy />} />
        <Route path="/data-processing" element={<DataProcessingAddendum />} />
        <Route path="/shopify-disclaimer" element={<ShopifyDisclaimer />} />
        <Route path="/customer-authorization" element={<CustomerAuthorization />} />
        {/* PROOF Legal */}
        <Route path="/legal/proof-module-terms" element={<ProofModuleTerms />} />
        {/* Risk Disclosure */}
        <Route path="/legal/risk-disclosure" element={<RiskDisclosure />} />
        {/* Legacy aliases */}
        <Route path="/legal/terms" element={<TermsOfService />} />
        <Route path="/legal/privacy" element={<PrivacyPolicy />} />
        <Route path="/legal/acceptable-use" element={<AcceptableUse />} />
        {/* Legacy intake pages (/get-started, /book) were tied to prior positioning
            and have been removed. Unmatched routes fall through to the catch-all Home. */}
        {/* Risk Intelligence Public Blog */}
        <Route path="/risk-intelligence" element={<RiskIntelligenceBlog />} />
        <Route path="/risk-intelligence/products" element={<RiskIntelligenceProducts />} />
        <Route path="/risk-intelligence/report/:reportId" element={<RiskIntelligenceReport />} />
        <Route path="/risk-intelligence/checkout/success" element={<RiskIntelligenceCheckoutSuccess />} />
        <Route path="/risk-intelligence/:category" element={<RiskIntelligenceCategory />} />
        <Route path="/risk-intelligence/:category/:slug" element={<RiskIntelligenceArticle />} />
        <Route path="*" element={<Home />} />
      </Routes>
    </Suspense>
  </MainLayout>
);

// Admin routes without MainLayout
const AdminRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/admin/ecom-protect/login" element={<EcomLogin />} />
      <Route path="/admin/ecom-protect/reset-password" element={<EcomResetPassword />} />
      <Route path="/admin/ecom-protect/*" element={<EcomLayout />}>
        <Route index element={<EcomDashboard />} />
        <Route path="clients" element={<EcomClients />} />
        <Route path="clients/:clientId" element={<EcomClientDetail />} />
        <Route path="connections" element={<EcomConnections />} />
        <Route path="cases" element={<EcomCases />} />
        <Route path="escalations" element={<EcomEscalations />} />
        <Route path="reports" element={<EcomReports />} />
        <Route path="settings" element={<EcomSettings />} />
        <Route path="test" element={<EcomTestTools />} />
        <Route path="scheduler" element={<EcomScheduler />} />
        <Route path="webhooks" element={<EcomWebhooks />} />
        <Route path="leads" element={<AdminLeads />} />
        <Route path="email-gateway" element={<EcomEmailGateway />} />
        <Route path="disputes" element={<EcomDisputes />} />
      </Route>
    </Routes>
  </Suspense>
);

// Portal routes without MainLayout
const PortalRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/portal/login" element={<PortalLogin />} />
      <Route path="/portal/reset-password" element={<PortalResetPassword />} />
      <Route path="/portal/invite/:token" element={<InviteAccept />} />
      <Route path="/portal/onboarding" element={<PortalOnboarding />} />
      <Route path="/portal/*" element={<PortalLayout />}>
        <Route index element={<PortalDashboard />} />
        <Route path="connections" element={<PortalConnections />} />
        <Route path="policies" element={<PortalPolicies />} />
        <Route path="cases" element={<PortalCases />} />
        <Route path="recovery" element={<PortalRecovery />} />
        <Route path="reports" element={<PortalReports />} />
        <Route path="settings" element={<PortalSettings />} />
      </Route>
    </Routes>
  </Suspense>
);

// Route selector based on path
const AppRoutes = () => {
  const location = useLocation();
  
  // Check route types
  const isLoginRoute = location.pathname === '/login';
  const isSignupRoute = location.pathname === '/signup';
  const isSwitchboardRoute = location.pathname === '/app';
  const isAppNotAvailable = location.pathname === '/app-not-available';
  const isFreeAuditTool = location.pathname === '/free-audit-tool';
  const isLegalHub = location.pathname === '/legal';
  const isAdminHealth = location.pathname === '/app/admin/health';
  const isCustomerDashboard = location.pathname.startsWith('/ecom/dashboard') || 
    location.pathname.startsWith('/inkflo/dashboard') ||
    location.pathname.startsWith('/local/dashboard');
  const isUnifiedAdminRoute = location.pathname === '/admin' || 
    (location.pathname.startsWith('/admin/') && 
     !location.pathname.startsWith('/admin/ecom-protect'));
  const isLegacyAdminRoute = location.pathname.startsWith('/admin/ecom-protect');
  const isPortalRoute = location.pathname.startsWith('/portal/');
  const isInkFloAdminRoute = location.pathname === '/inkflo/admin' || 
    location.pathname.startsWith('/inkflo/admin/') ||
    location.pathname === '/inkflo/shop' ||
    location.pathname.startsWith('/inkflo/shop/');
  const isManifestGuardAppRoute = location.pathname.startsWith('/app/manifestguard');
  const isVendorGuardAppRoute = location.pathname.startsWith('/app/vendorguard');
  const isGrantGuardAppRoute = location.pathname.startsWith('/app/grantguard');
  const isCouncilGuardAppRoute = location.pathname.startsWith('/app/councilguard');
  const isProofAppRoute = location.pathname.startsWith('/app/proof');
  const isRiskAdvisorAppRoute = location.pathname.startsWith('/app/risk-dashboard') || location.pathname.startsWith('/app/risk-advisor');
  const isCOAAdminRoute = location.pathname.startsWith('/app/admin/coa');
  const isProofSessionRoute = location.pathname.startsWith('/proof/session');
  const isProofDemoRoute = location.pathname === '/proof/demo';
  const isProofSharedRoute = location.pathname.startsWith('/proof/record/');
  const isProofWhyRoute = location.pathname === '/proof/why-proof';
  const isProofPricingRoute = location.pathname === '/proof/pricing';
  const isEcomProtectLanding = location.pathname === '/ecom-protect';
  const isGrantGuardTribalLanding = location.pathname === '/grantguard-tribal';
  const isRiskIntelligenceRoute = location.pathname.startsWith('/admin/risk-intelligence');
  
  // Route to appropriate handler
  if (isLoginRoute || isSignupRoute) return <LoginRoute />;
  if (isSwitchboardRoute) return <SwitchboardRoute />;
  if (isAppNotAvailable) return <AppNotAvailableRoute />;
  if (isFreeAuditTool) return <FreeAuditToolRoute />;
  if (isLegalHub) return <LegalHubRoute />;
  if (isAdminHealth) return <AdminHealthRoute />;
  if (isCustomerDashboard) return <CustomerDashboardRoutes />;
  if (isManifestGuardAppRoute) return <ManifestGuardAppRoutes />;
  if (isVendorGuardAppRoute) return <VendorGuardAppRoutes />;
  if (isRiskAdvisorAppRoute) return <RiskAdvisorAppRoutes />;
  if (isGrantGuardAppRoute) return <Suspense fallback={<PageLoader />}><GrantGuardAppRoutes /></Suspense>;
  if (isCouncilGuardAppRoute) return <Suspense fallback={<PageLoader />}><CouncilGuardAppRoutes /></Suspense>;
  if (isProofAppRoute) return <ProofAppRoutes />;
  if (isCOAAdminRoute) return <COAAdminRoutes />;
  if (isProofSessionRoute) return <ProofSessionRoutes />;
  if (isProofDemoRoute) return <ProofDemoRoute />;
  if (isProofSharedRoute) return <ProofSharedRoute />;
  if (isProofWhyRoute) return <ProofWhyRoute />;
  if (isProofPricingRoute) return <ProofPricingRoute />;
  if (isEcomProtectLanding) return <EcomProtectLandingRoute />;
  if (isGrantGuardTribalLanding) return <GrantGuardTribalLandingRoute />;
  if (isRiskIntelligenceRoute) return <RiskIntelligenceRoutes />;
  if (isUnifiedAdminRoute) return <UnifiedAdminRoutes />;
  if (isLegacyAdminRoute) return <AdminRoutes />;
  if (isPortalRoute) return <PortalRoutes />;
  if (isInkFloAdminRoute) return <InkFloRoutes />;
  return <MainRoutes />;
};

// Login Route
const LoginRoute = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/login" element={<UnifiedLogin />} />
      <Route path="/signup" element={<Signup />} />
    </Routes>
  </Suspense>
);

// Switchboard Route (Central /app landing)
const SwitchboardRoute = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/app" element={<Switchboard />} />
    </Routes>
  </Suspense>
);

// App Not Available Route
const AppNotAvailableRoute = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/app-not-available" element={<AppNotAvailable />} />
    </Routes>
  </Suspense>
);

// Free Audit Tool Route (standalone, no MainLayout header/footer)
const FreeAuditToolRoute = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/free-audit-tool" element={<FreeAuditTool />} />
    </Routes>
  </Suspense>
);

// Legal Hub Route (standalone comprehensive legal center)
const LegalHubRoute = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/legal" element={<LegalHub />} />
    </Routes>
  </Suspense>
);

// Admin Health Check Route (requires admin auth)
const AdminHealthRoute = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/app/admin/health" element={<AdminHealthCheck />} />
    </Routes>
  </Suspense>
);

// Customer Dashboard Routes (per-app dashboards)
// Uses CustomerRouteGuard to prevent access to placeholder dashboards
const CustomerRouteGuard = lazy(() => import("./components/CustomerRouteGuard"));

const CustomerDashboardRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/ecom/dashboard" element={
        <CustomerRouteGuard appId="ecom">
          <CustomerEcomDashboard />
        </CustomerRouteGuard>
      } />
      <Route path="/inkflo/dashboard" element={
        <CustomerRouteGuard appId="inkflo">
          <InkFloCustomerDashboard />
        </CustomerRouteGuard>
      } />
      <Route path="/inkflo/dashboard/shops/:shopId" element={
        <CustomerRouteGuard appId="inkflo">
          <InkFloCustomerShopDetail />
        </CustomerRouteGuard>
      } />
      {/* Legacy /local/dashboard removed */}
    </Routes>
  </Suspense>
);

// Unified Admin Portal routes
const UnifiedAdminRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<AdminHome />} />
        <Route path="ecom" element={<EcomAdminRedirect />} />
        <Route path="inkflo" element={<InkFloAdminRedirect />} />
        {/* Legacy /admin/local removed */}
        <Route path="settings" element={<AdminSettings />} />
        <Route path="automation" element={<AdminAutomationDashboard />} />
        <Route path="evidence" element={<AdminEvidenceDashboard />} />
        <Route path="ai-agents" element={<AIAgentsHub />} />
        <Route path="alerts" element={<AdminAlerts />} />
        <Route path="risk-dashboard" element={<RiskDashboard isAdmin={true} />} />
        <Route path="risk-advisor" element={<RiskAdvisorChat isAdmin={true} />} />
        <Route path="knowledge-vault" element={<KnowledgeVault />} />
        <Route path="social-publisher" element={<SocialPublisher />} />
      </Route>
    </Routes>
  </Suspense>
);

// InkFlo admin routes without MainLayout
const InkFloRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/inkflo/admin" element={<InkFloAdmin />} />
      <Route path="/inkflo/shop" element={<InkFloShop />} />
    </Routes>
  </Suspense>
);

// Risk Intelligence Engine routes (authenticated admin)
const RiskIntelligenceRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/admin/risk-intelligence" element={<RiskIntelligenceLayout />}>
        <Route index element={<RiskIntelligenceDashboard />} />
        <Route path="content" element={<RiskIntelligenceContent />} />
        <Route path="leads" element={<RiskIntelligenceLeads />} />
        <Route path="automation" element={<RiskIntelligenceAutomation />} />
        <Route path="reports" element={<RiskIntelligenceReports />} />
        <Route path="crm" element={<RiskIntelligenceLeadsCRM />} />
        <Route path="search-intelligence" element={<RiskIntelligenceSearchIntel />} />
        <Route path="social" element={<RiskIntelligenceSocial />} />
      </Route>
    </Routes>
  </Suspense>
);

// PROOF app routes (authenticated)
const ProofAppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/app/proof/*" element={<ProofLayout />}>
        <Route index element={<ProofDashboard />} />
        <Route path="dashboard" element={<ProofDashboard />} />
        <Route path="new" element={<ProofNewRecord />} />
        <Route path="records/:recordId" element={<ProofRecordDetail />} />
        <Route path="records/:recordId/completed" element={<ProofRecordCompleted />} />
        <Route path="vendors" element={<ProofVendors />} />
        <Route path="compliance" element={<ProofComplianceDashboard />} />
        <Route path="compliance/settings" element={<ProofComplianceSettings />} />
      </Route>
    </Routes>
  </Suspense>
);

// COA Admin routes (authenticated, internal)
const COAAdminRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/app/admin/coa" element={<COAAdmin />} />
      <Route path="/app/admin/coa/tiers" element={<COATierManagement />} />
    </Routes>
  </Suspense>
);

// PROOF public session route (no auth required, token protected)
const ProofSessionRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/proof/session/:token" element={<ProofCustomerSession />} />
    </Routes>
  </Suspense>
);

// PROOF public demo route (no auth required)
const ProofDemoRoute = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/proof/demo" element={<ProofDemo />} />
    </Routes>
  </Suspense>
);

// PROOF shared record route (public, token protected)
const ProofSharedRoute = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/proof/record/:shareToken" element={<ProofSharedRecord />} />
    </Routes>
  </Suspense>
);

// PROOF why-proof education page (public)
const ProofWhyRoute = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/proof/why-proof" element={<ProofWhyProof />} />
    </Routes>
  </Suspense>
);

// PROOF pricing page (public)
const ProofPricingRoute = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/proof/pricing" element={<ProofPricing />} />
    </Routes>
  </Suspense>
);

// ECom Protect standalone landing page (public, no main layout)
const EcomProtectLandingRoute = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/ecom-protect" element={<EcomProtectLanding />} />
    </Routes>
  </Suspense>
);

// GrantGuard Tribal standalone landing page (public, no main layout)
const GrantGuardTribalLandingRoute = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/grantguard-tribal" element={<GrantGuardTribal />} />
    </Routes>
  </Suspense>
);

// ManifestGuard app routes without MainLayout
const ManifestGuardAppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/app/manifestguard" element={<ManifestGuardLayout />}>
        <Route index element={<ManifestGuardDashboard />} />
        <Route path="new" element={<ManifestGuardNewShipment />} />
        <Route path="shipments" element={<ManifestGuardShipmentsList />} />
        <Route path="shipments/:shipmentId" element={<ManifestGuardShipmentDetail />} />
        <Route path="audit" element={<ManifestGuardAuditLog />} />
        <Route path="settings" element={<ManifestGuardSettings />} />
      </Route>
    </Routes>
  </Suspense>
);

// VendorGuard app routes without MainLayout
const VendorGuardAppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/app/vendorguard" element={<VendorGuardLayout />}>
        <Route index element={<VendorGuardDashboard />} />
        <Route path="vendors" element={<VendorGuardVendors />} />
        <Route path="vendors/new" element={<VendorGuardAddVendor />} />
        <Route path="vendors/:vendorId" element={<VendorGuardVendorDetail />} />
        <Route path="audit" element={<VendorGuardAuditLog />} />
        <Route path="settings" element={<VendorGuardSettings />} />
      </Route>
    </Routes>
  </Suspense>
);

// Risk Advisor app routes without MainLayout
const RiskAdvisorAppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path="/app/risk-dashboard" element={<RiskDashboard isAdmin={false} />} />
      <Route path="/app/risk-advisor" element={<RiskAdvisorChat isAdmin={false} />} />
    </Routes>
  </Suspense>
);

function App({ onMount }) {
  useEffect(() => {
    // Signal that app has mounted
    if (onMount) {
      onMount();
    }
    
    // Also call global function if available
    if (typeof window.__markAppLoaded__ === 'function') {
      window.__markAppLoaded__();
    }
  }, [onMount]);

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollToTop />
          <AppRoutes />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
